import axios from 'axios';
import {
  createContext,
  createElement,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { defaultInstance } from 'src/api/instance';
import { configVars } from 'src/helpers';
import { IFraudTrendEvidence, IListCheckEntry } from 'src/ts/interfaces/listCheck';

type FraudTrendEvidenceStatus = 'idle' | 'loading' | 'success' | 'missing' | 'error';

export const EVIDENCE_FILES = { evidences: 'evidences', visual: 'text_boxes' } as const;

export type FraudTrendEvidenceFile = (typeof EVIDENCE_FILES)[keyof typeof EVIDENCE_FILES];

export interface FraudTrendEvidenceResult {
  status: FraudTrendEvidenceStatus;
  data: IFraudTrendEvidence | null;
}

export interface FraudTrendEvidenceState {
  load: (proofId: string, entry: IListCheckEntry) => void;
  evidenceFor: (
    proofId: string | undefined,
    checkKey: string,
    file: FraudTrendEvidenceFile
  ) => FraudTrendEvidenceResult;
}

const IDLE: FraudTrendEvidenceResult = { status: 'idle', data: null };
const LOADING: FraudTrendEvidenceResult = { status: 'loading', data: null };
const FAILED: FraudTrendEvidenceResult = { status: 'error', data: null };
const MISSING: FraudTrendEvidenceResult = { status: 'missing', data: null };

const checkOf = (proofId: string, checkKey: string) => `${proofId}|${checkKey}`;

const keyOf = (
  proofId: string,
  evaluationId: string,
  checkKey: string,
  file: FraudTrendEvidenceFile
) => `${proofId}|${evaluationId}|${checkKey}|${file}`;

const evidenceUrl = (proofId: string, checkKey: string, file: FraudTrendEvidenceFile) =>
  `${configVars.server_url}/v2/proofs/${proofId}/list_check_rulings/${encodeURIComponent(
    checkKey
  )}/evidence?file=${file}`;

const useEvidenceStore = (): FraudTrendEvidenceState => {
  const requestedRef = useRef<Set<string>>(new Set());
  const cancelSourceRef = useRef(axios.CancelToken.source());
  const [results, setResults] = useState<Record<string, FraudTrendEvidenceResult>>({});
  const [evaluations, setEvaluations] = useState<Record<string, string>>({});

  useEffect(() => {
    const cancelSource = cancelSourceRef.current;
    return () => {
      cancelSource.cancel();
    };
  }, []);

  const fetchFile = useCallback(
    async (proofId: string, checkKey: string, file: FraudTrendEvidenceFile, key: string) => {
      let outcome: FraudTrendEvidenceResult;
      try {
        const body = (await defaultInstance.get(evidenceUrl(proofId, checkKey, file), {
          cancelToken: cancelSourceRef.current.token
        })) as unknown as IFraudTrendEvidence;
        outcome = { status: 'success', data: body };
      } catch (error) {
        if (axios.isCancel(error)) return;
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          outcome = MISSING;
        } else {
          requestedRef.current.delete(key);
          outcome = FAILED;
        }
      }
      setResults((prev) => ({ ...prev, [key]: outcome }));
    },
    []
  );

  const load = useCallback(
    (proofId: string, entry: IListCheckEntry) => {
      const evaluationId = entry.evaluation_id ?? '';
      const checkKey = entry.check_key;
      setEvaluations((prev) =>
        prev[checkOf(proofId, checkKey)] === evaluationId
          ? prev
          : { ...prev, [checkOf(proofId, checkKey)]: evaluationId }
      );
      const files: FraudTrendEvidenceFile[] = [
        ...(entry.has_evidence ? [EVIDENCE_FILES.evidences] : []),
        ...(entry.has_visual ? [EVIDENCE_FILES.visual] : [])
      ];
      files
        .map((file) => ({ file, key: keyOf(proofId, evaluationId, checkKey, file) }))
        .filter(({ key }) => !requestedRef.current.has(key))
        .forEach(({ file, key }) => {
          requestedRef.current.add(key);
          setResults((prev) => ({ ...prev, [key]: LOADING }));
          fetchFile(proofId, checkKey, file, key);
        });
    },
    [fetchFile]
  );

  const evidenceFor = useCallback(
    (proofId: string | undefined, checkKey: string, file: FraudTrendEvidenceFile) => {
      if (!proofId) return IDLE;
      const evaluationId = evaluations[checkOf(proofId, checkKey)];
      if (evaluationId === undefined) return IDLE;
      return results[keyOf(proofId, evaluationId, checkKey, file)] ?? IDLE;
    },
    [evaluations, results]
  );

  return useMemo(() => ({ load, evidenceFor }), [load, evidenceFor]);
};

const FraudTrendEvidenceContext = createContext<FraudTrendEvidenceState | null>(null);

export const FraudTrendEvidenceProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const store = useEvidenceStore();
  return createElement(FraudTrendEvidenceContext.Provider, { value: store }, children);
};

export const useFraudTrendEvidence = (): FraudTrendEvidenceState => {
  const shared = useContext(FraudTrendEvidenceContext);
  const local = useEvidenceStore();
  return shared ?? local;
};
